import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

// Portfolio Item Component
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const PortfolioItem: React.FC<{
  item: {
    id: number;
    title: string;
    category: string;
    description: string;
    image: string;
    year: string;
  };
  index: number;
}> = ({ item, index }) => {
  const [cardRef, cardVisible] = useScrollAnimation(0.2);
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div 
      ref={cardRef}
      className={`group relative overflow-hidden rounded-xl sm:rounded-2xl transition-all duration-500 hover:shadow-2xl hover:shadow-brand-orange/20 hover:-translate-y-2 ${
        cardVisible 
          ? 'opacity-100 translate-y-0' 
          : 'opacity-0 translate-y-10'
      }`}
      style={{ 
        animationDelay: `${index * 100}ms`,
        transitionDelay: `${index * 100}ms`
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Image Container */}
      <div className="relative h-64 sm:h-80 overflow-hidden">
        <img 
          src={item.image} 
          alt={item.title}
          className="w-full h-full object-cover transition-all duration-500 group-hover:scale-110"
          onError={(e) => {
            // Fallback to a branded placeholder if image fails to load
            const target = e.target as HTMLImageElement;
            target.src = `data:image/svg+xml;base64,${btoa(`
              <svg width="400" height="300" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" style="stop-color:#f97316;stop-opacity:1" />
                    <stop offset="100%" style="stop-color:#ea580c;stop-opacity:1" />
                  </linearGradient>
                </defs>
                <rect width="100%" height="100%" fill="url(#grad)"/>
                <circle cx="200" cy="120" r="30" fill="white" opacity="0.2"/>
                <text x="200" y="180" text-anchor="middle" font-family="Arial, sans-serif" font-size="16" font-weight="bold" fill="white">
                  ${item.title}
                </text>
                <text x="200" y="200" text-anchor="middle" font-family="Arial, sans-serif" font-size="12" fill="white" opacity="0.8">
                  ${item.category}
                </text>
              </svg>
            `)}`;
          }}
        />
        
        {/* Overlay */}
        <div className={`absolute inset-0 bg-gradient-to-t from-brand-primary/90 via-brand-primary/50 to-transparent transition-all duration-300 ${
          isHovered ? 'opacity-100' : 'opacity-0'
        }`}>
          <div className="absolute bottom-4 left-4 right-4 text-white">
            <h3 className="text-lg sm:text-xl font-bold mb-2">{item.title}</h3>
            <p className="text-sm sm:text-base opacity-90 mb-2">{item.description}</p>
            <div className="flex justify-between items-center text-xs sm:text-sm">
              <span className="bg-brand-orange px-2 py-1 rounded-full">{item.category}</span>
              <span className="opacity-75">{item.year}</span>
            </div>
          </div>
        </div>

      </div>

      {/* Content */}
      <div className="p-4 sm:p-6 bg-white">
        <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 transition-all duration-300 group-hover:text-brand-primary">
          {item.title}
        </h3>
        <p className="text-brand-orange font-semibold mb-2 text-sm sm:text-base">
          {item.category}
        </p>
        <p className="text-gray-600 text-sm sm:text-base leading-relaxed">
          {item.description}
        </p>
        <div className="flex justify-end items-center mt-4 pt-4 border-t border-gray-100">
          <span className="text-sm text-gray-500">{item.year}</span>
        </div>
      </div>
    </div>
  );
};

// Modern 3D Designs Card Component
const Creative3DDesignsCard: React.FC<{ index: number }> = ({ index }) => {
  const [cardRef, cardVisible] = useScrollAnimation(0.2);

  return (
    <div 
      ref={cardRef}
      className={`group relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 overflow-hidden ${
        cardVisible 
          ? 'opacity-100 translate-y-0' 
          : 'opacity-0 translate-y-10'
      }`}
      style={{ 
        animationDelay: `${index * 100}ms`,
        transitionDelay: `${index * 100}ms`,
        height: '520px'
      }}
    >
      {/* Image Container */}
      <div className="relative h-64 overflow-hidden">
        <img 
          src="/Our Creative 3D Designs/0017adba-ece5-4d9d-b1fa-2e1389c35016.jpg" 
          alt="3D Design Showcase"
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
        />
        
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div className="absolute bottom-4 left-4 right-4 text-white">
            <div className="flex items-center space-x-2 mb-2">
              <span className="text-2xl">🎨</span>
              <span className="text-sm font-semibold">3D Creative Designs</span>
            </div>
            <span className="text-xs bg-orange-500 px-2 py-1 rounded-full">60+ Designs</span>
          </div>
        </div>

        {/* Floating Badge */}
        <div className="absolute top-4 right-4 w-8 h-8 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-500">
          <span className="text-orange-600 text-xs font-bold">3D</span>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 flex flex-col h-64">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-yellow-500 rounded-xl flex items-center justify-center text-white text-xl">
            🎨
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900">3D Designs</h3>
            <p className="text-orange-600 text-sm font-medium">3D Creative Designs</p>
          </div>
        </div>
        
        <p className="text-gray-600 text-sm leading-relaxed mb-6 flex-grow">
          Innovative 3D designs and creative solutions that bring brands to life.
        </p>
        
        <div className="flex justify-between items-center pt-4 border-t border-gray-100 mt-auto">
          <div className="flex items-center space-x-2 text-xs text-gray-500">
            <span>60+ Designs</span>
            <span>•</span>
            <span>2024</span>
          </div>
          <Link 
            to="/3d-designs"
            className="bg-gradient-to-r from-orange-500 to-yellow-500 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:shadow-lg hover:shadow-orange-500/25 transition-all duration-300 hover:scale-105 group"
          >
            <span className="flex items-center space-x-1">
              <span>Explore</span>
              <svg className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </span>
          </Link>
        </div>
      </div>
    </div>
  );
};

// Modern Brand Activation Card Component
const BrandActivationCard: React.FC<{ index: number }> = ({ index }) => {
  const [cardRef, cardVisible] = useScrollAnimation(0.2);

  return (
    <div 
      ref={cardRef}
      className={`group relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 overflow-hidden ${
        cardVisible 
          ? 'opacity-100 translate-y-0' 
          : 'opacity-0 translate-y-10'
      }`}
      style={{ 
        animationDelay: `${index * 100}ms`,
        transitionDelay: `${index * 100}ms`,
        height: '520px'
      }}
    >
      {/* Image Container */}
      <div className="relative h-64 overflow-hidden">
        <img 
          src="/Brand Activation/4d115046-bb70-4649-acfd-63f313baccbb.jpg" 
          alt="Brand Activation Showcase"
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
        />
        
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div className="absolute bottom-4 left-4 right-4 text-white">
            <div className="flex items-center space-x-2 mb-2">
              <span className="text-2xl">🎯</span>
              <span className="text-sm font-semibold">Brand Activation</span>
            </div>
            <span className="text-xs bg-yellow-500 px-2 py-1 rounded-full">42+ Campaigns</span>
          </div>
        </div>

        {/* Floating Badge */}
        <div className="absolute top-4 right-4 w-8 h-8 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-500">
          <span className="text-yellow-600 text-xs font-bold">BA</span>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 flex flex-col h-64">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-12 h-12 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-xl flex items-center justify-center text-white text-xl">
            🎯
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900">Brand Activation</h3>
            <p className="text-yellow-600 text-sm font-medium">Experiential Marketing</p>
          </div>
        </div>
        
        <p className="text-gray-600 text-sm leading-relaxed mb-6 flex-grow">
          Comprehensive brand activation campaigns.
        </p>
        
        <div className="flex justify-between items-center pt-4 border-t border-gray-100 mt-auto">
          <div className="flex items-center space-x-2 text-xs text-gray-500">
            <span>42+ Campaigns</span>
            <span>•</span>
            <span>2024</span>
          </div>
          <Link 
            to="/brand-activation"
            className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:shadow-lg hover:shadow-yellow-500/25 transition-all duration-300 hover:scale-105 group"
          >
            <span className="flex items-center space-x-1">
              <span>Explore</span>
              <svg className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </span>
          </Link>
        </div>
      </div>
    </div>
  );
};

// Modern Float Activation Card Component
const FloatActivationCard: React.FC<{ index: number }> = ({ index }) => {
  const [cardRef, cardVisible] = useScrollAnimation(0.2);

  return (
    <div 
      ref={cardRef}
      className={`group relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 overflow-hidden ${
        cardVisible 
          ? 'opacity-100 translate-y-0' 
          : 'opacity-0 translate-y-10'
      }`}
      style={{ 
        animationDelay: `${index * 100}ms`,
        transitionDelay: `${index * 100}ms`,
        height: '520px'
      }}
    >
      {/* Image Container */}
      <div className="relative h-64 overflow-hidden">
        <img 
          src="/Float Activation/e6b8b2c0-1455-4839-bcad-d5ec82e38390.jpg" 
          alt="Float Activation Showcase"
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
        />
        
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div className="absolute bottom-4 left-4 right-4 text-white">
            <div className="flex items-center space-x-2 mb-2">
              <span className="text-2xl">🎈</span>
              <span className="text-sm font-semibold">Float Activation</span>
            </div>
            <span className="text-xs bg-orange-500 px-2 py-1 rounded-full">13+ Campaigns</span>
          </div>
        </div>

        {/* Floating Badge */}
        <div className="absolute top-4 right-4 w-8 h-8 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-500">
          <span className="text-orange-600 text-xs font-bold">FA</span>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 flex flex-col h-64">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-yellow-500 rounded-xl flex items-center justify-center text-white text-xl">
            🎈
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900">Float Activation</h3>
            <p className="text-orange-600 text-sm font-medium">Aerial Marketing</p>
          </div>
        </div>
        
        <p className="text-gray-600 text-sm leading-relaxed mb-6 flex-grow">
          Innovative float activation campaigns and aerial marketing solutions.
        </p>
        
        <div className="flex justify-between items-center pt-4 border-t border-gray-100 mt-auto">
          <div className="flex items-center space-x-2 text-xs text-gray-500">
            <span>13+ Campaigns</span>
            <span>•</span>
            <span>2024</span>
          </div>
          <Link 
            to="/float-activation"
            className="bg-gradient-to-r from-orange-500 to-yellow-500 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:shadow-lg hover:shadow-orange-500/25 transition-all duration-300 hover:scale-105 group"
          >
            <span className="flex items-center space-x-1">
              <span>Explore</span>
              <svg className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </span>
          </Link>
        </div>
      </div>
    </div>
  );
};

// Modern Events Card Component
const EventsCard: React.FC<{ index: number }> = ({ index }) => {
  const [cardRef, cardVisible] = useScrollAnimation(0.2);

  return (
    <div 
      ref={cardRef}
      className={`group relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 overflow-hidden ${
        cardVisible 
          ? 'opacity-100 translate-y-0' 
          : 'opacity-0 translate-y-10'
      }`}
      style={{ 
        animationDelay: `${index * 100}ms`,
        transitionDelay: `${index * 100}ms`,
        height: '520px'
      }}
    >
      {/* Image Container */}
      <div className="relative h-64 overflow-hidden">
        <img 
          src="/Events/5b5ac1cd-d46e-4084-9b50-23e778141739.jpg" 
          alt="Events Showcase"
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
        />
        
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div className="absolute bottom-4 left-4 right-4 text-white">
            <div className="flex items-center space-x-2 mb-2">
              <span className="text-2xl">🎉</span>
              <span className="text-sm font-semibold">Events</span>
            </div>
            <span className="text-xs bg-yellow-500 px-2 py-1 rounded-full">40+ Events</span>
          </div>
        </div>

        {/* Floating Badge */}
        <div className="absolute top-4 right-4 w-8 h-8 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-500">
          <span className="text-yellow-600 text-xs font-bold">EV</span>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 flex flex-col h-64">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-12 h-12 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-xl flex items-center justify-center text-white text-xl">
            🎉
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900">Events</h3>
            <p className="text-yellow-600 text-sm font-medium">Brand Experiences</p>
          </div>
        </div>
        
        <p className="text-gray-600 text-sm leading-relaxed mb-6 flex-grow">
          Spectacular events and memorable brand experiences that drive engagement.
        </p>
        
        <div className="flex justify-between items-center pt-4 border-t border-gray-100 mt-auto">
          <div className="flex items-center space-x-2 text-xs text-gray-500">
            <span>40+ Events</span>
            <span>•</span>
            <span>2024</span>
          </div>
          <Link 
            to="/events"
            className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:shadow-lg hover:shadow-yellow-500/25 transition-all duration-300 hover:scale-105 group"
          >
            <span className="flex items-center space-x-1">
              <span>Explore</span>
              <svg className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </span>
          </Link>
        </div>
      </div>
    </div>
  );
};

const Portfolio: React.FC = () => {
  const [headerRef, headerVisible] = useScrollAnimation(0.3);

  return (
    <section id="portfolio" className="relative py-8 sm:py-12 lg:py-16 bg-white overflow-hidden">
      {/* Modern Background Pattern */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-orange-50/30 via-transparent to-yellow-50/30"></div>
        <div className="absolute top-0 left-0 w-full h-full bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0iZ3JpZCIgd2lkdGg9IjYwIiBoZWlnaHQ9IjYwIiBwYXR0ZXJuVW5pdHM9InVzZXJTcGFjZU9uVXNlIj48cGF0aCBkPSJNIDYwIDAgTCAwIDAgMCA2MCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSJyZ2JhKDI0MywgMTE0LCAyMiwgMC4wNSkiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-40"></div>
        <div className="absolute top-20 right-20 w-72 h-72 bg-gradient-to-r from-orange-200/20 to-yellow-200/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-gradient-to-r from-yellow-200/20 to-orange-200/20 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Modern Header */}
        <div 
          ref={headerRef}
          className={`text-center mb-12 sm:mb-16 lg:mb-20 transition-all duration-1000 ${
            headerVisible 
              ? 'opacity-100 translate-y-0' 
              : 'opacity-0 translate-y-10'
          }`}
        >
          {/* Modern Badge */}
          <div className="inline-flex items-center space-x-3 bg-gradient-to-r from-orange-100 to-yellow-100 border border-orange-200/50 rounded-full px-6 py-3 text-orange-700 text-sm font-semibold mb-8 shadow-lg shadow-orange-100/50">
            <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></div>
            <span>Our Portfolio</span>
            <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse" style={{ animationDelay: '0.5s' }}></div>
          </div>

          <h2 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold mb-6">
            <span className="bg-gradient-to-r from-gray-900 via-orange-600 to-gray-900 bg-clip-text text-transparent">
              Our Work
            </span>
          </h2>
          
          {/* Modern Divider */}
          <div className="flex items-center justify-center space-x-4 mb-8">
            <div className="w-16 h-0.5 bg-gradient-to-r from-transparent to-orange-400"></div>
            <div className="w-3 h-3 bg-gradient-to-r from-orange-400 to-yellow-400 rounded-full"></div>
            <div className="w-16 h-0.5 bg-gradient-to-l from-transparent to-yellow-400"></div>
          </div>
          
          <p className="text-lg sm:text-xl lg:text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            From <span className="text-orange-600 font-semibold">3D designs</span> to <span className="text-yellow-600 font-semibold">spectacular events</span>, 
            discover how we've helped brands soar higher with innovative campaigns.
          </p>
        </div>

        {/* Modern Portfolio Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 lg:gap-10 mb-16 sm:mb-20 lg:mb-24">
          {/* Special 3D Designs Card - Always show first */}
          <Creative3DDesignsCard index={0} />
          
          {/* Special Brand Activation Card - Always show second */}
          <BrandActivationCard index={1} />
          
          {/* Special Float Activation Card - Always show third */}
          <FloatActivationCard index={2} />
          
          {/* Special Events Card - Always show fourth */}
          <EventsCard index={3} />
        </div>

        {/* Modern Stats Section */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-16 sm:mb-20 lg:mb-24">
          <div className="text-center p-6 bg-gradient-to-br from-orange-50 to-orange-100 rounded-2xl border border-orange-200/50 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
            <div className="text-3xl sm:text-4xl lg:text-5xl font-bold text-orange-600 mb-2">60+</div>
            <div className="text-sm sm:text-base text-gray-700 font-medium">3D Designs</div>
          </div>
          <div className="text-center p-6 bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-2xl border border-yellow-200/50 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
            <div className="text-3xl sm:text-4xl lg:text-5xl font-bold text-yellow-600 mb-2">40+</div>
            <div className="text-sm sm:text-base text-gray-700 font-medium">Brand Activations</div>
          </div>
          <div className="text-center p-6 bg-gradient-to-br from-orange-50 to-orange-100 rounded-2xl border border-orange-200/50 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
            <div className="text-3xl sm:text-4xl lg:text-5xl font-bold text-orange-600 mb-2">13+</div>
            <div className="text-sm sm:text-base text-gray-700 font-medium">Float Activations</div>
          </div>
          <div className="text-center p-6 bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-2xl border border-yellow-200/50 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
            <div className="text-3xl sm:text-4xl lg:text-5xl font-bold text-yellow-600 mb-2">40+</div>
            <div className="text-sm sm:text-base text-gray-700 font-medium">Events</div>
          </div>
        </div>
        
        {/* Modern CTA Section */}
        <div className="relative">
          <div className="bg-gradient-to-r from-orange-600 via-yellow-500 to-orange-600 rounded-3xl p-8 sm:p-12 lg:p-16 text-white relative overflow-hidden">
            {/* Modern Background Pattern */}
            <div className="absolute inset-0 opacity-20">
              <div className="absolute top-0 left-0 w-full h-full bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0iZ3JpZCIgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBwYXR0ZXJuVW5pdHM9InVzZXJTcGFjZU9uVXNlIj48cGF0aCBkPSJNIDQwIDAgTCAwIDAgMCA0MCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSJyZ2JhKDI1NSwyNTUsMjU1LDAuMykiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')]"></div>
            </div>
            
            <div className="relative z-10 text-center">
              <h3 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
                Ready to Create Something 
                <span className="block bg-gradient-to-r from-yellow-200 to-white bg-clip-text text-transparent">
                  Extraordinary?
                </span>
              </h3>
              <p className="text-lg sm:text-xl lg:text-2xl mb-8 opacity-90 max-w-3xl mx-auto leading-relaxed">
                Let's discuss your next campaign and make your brand soar higher with innovative solutions.
              </p>
              
              <div className="flex justify-center">
                <button 
                  onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                  className="group bg-white text-orange-600 px-8 py-4 rounded-2xl font-bold text-lg hover:bg-gray-100 transition-all duration-300 hover:shadow-2xl hover:shadow-white/25 hover:scale-105"
                >
                  <span className="flex items-center justify-center space-x-2">
                    <span>Start Your Project</span>
                    <svg className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Portfolio;
